// === Session Utilities & Cache ===
// normalizeAgent, getSessionMeta, session cache CRUD

import { deepClone } from '../utils';
import { sessionState } from '../state';
import { logSessionDebug } from '../ui/session-debug';
import {
  mergeCachedHistoryWindow,
  summarizeHistoryCriticalMessages,
} from './history-window-merge';
import { cachedStreamingAssistantMatchesSnapshotMessage } from './streaming-assistant-match';
import {
  AGENT_LABELS, DEFAULT_AGENT,
  SESSION_CACHE_LIMIT, SESSION_CACHE_MAX_ENTRY_WEIGHT, SESSION_CACHE_MAX_WEIGHT,
} from '../constants';

function logSessionCache(_event: string, _payload: Record<string, unknown>): void {}

// ---------------------------------------------------------------------------
// Agent helpers
// ---------------------------------------------------------------------------

export function normalizeAgent(agent: unknown): string {
  return AGENT_LABELS[agent as string] ? (agent as string) : DEFAULT_AGENT;
}

export function getAgentSessionStorageKey(agent: string): string {
  return `webcoding-session-${normalizeAgent(agent)}`;
}

export function getAgentModeStorageKey(agent: string): string {
  return `webcoding-mode-${normalizeAgent(agent)}`;
}

export function getLastSessionForAgent(agent: string): string | null {
  return localStorage.getItem(getAgentSessionStorageKey(agent));
}

export function setLastSessionForAgent(agent: string, sessionId: string): void {
  localStorage.setItem(getAgentSessionStorageKey(agent), sessionId);
  localStorage.setItem('webcoding-session', sessionId);
}

export function getSessionMeta(sessionId: string) {
  return sessionState.sessions.find((s) => s.id === sessionId) ?? null;
}

// ---------------------------------------------------------------------------
// Message / snapshot utilities
// ---------------------------------------------------------------------------

export function cloneMessages(messages: unknown): unknown[] {
  return Array.isArray(messages) ? deepClone(messages) : [];
}

function findCachedStreamingAssistantMessage(sessionId: string): any {
  const cached = (sessionState.sessionCache.get(sessionId) as any)?.snapshot;
  const messages = Array.isArray(cached?.messages) ? cached.messages : [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role !== 'assistant') continue;
    const hasStreamingMeta = !!(message.firstResponseAt || message.lastAppendedAt);
    const hasStreamingContent = !!(
      String(message.content || '').trim() ||
      (Array.isArray(message.segments) && message.segments.length > 0) ||
      (Array.isArray(message.toolCalls) && message.toolCalls.length > 0)
    );
    if (hasStreamingMeta || hasStreamingContent) return deepClone(message);
  }
  return null;
}

function findCachedSessionMessages(sessionId: string): any[] {
  const cached = (sessionState.sessionCache.get(sessionId) as any)?.snapshot;
  return Array.isArray(cached?.messages) ? deepClone(cached.messages) : [];
}

function mergeAssistantMessageFields(target: any, cachedAssistant: any): void {
  if (!target.firstResponseAt && cachedAssistant.firstResponseAt) {
    target.firstResponseAt = cachedAssistant.firstResponseAt;
  }
  if (!target.lastAppendedAt && cachedAssistant.lastAppendedAt) {
    target.lastAppendedAt = cachedAssistant.lastAppendedAt;
  }
  if (!target.timestamp && cachedAssistant.timestamp) {
    target.timestamp = cachedAssistant.timestamp;
  }
  if (!target.model && cachedAssistant.model) {
    target.model = cachedAssistant.model;
  }
  if (!target.displayModel && cachedAssistant.displayModel) {
    target.displayModel = cachedAssistant.displayModel;
  }

  const targetText = String(target.content || '');
  const cachedText = String(cachedAssistant.content || '');
  if ((!targetText.trim() || cachedText.length > targetText.length) && cachedText.trim()) {
    target.content = cachedText;
  }

  const targetSegmentsSize = Array.isArray(target.segments) ? JSON.stringify(target.segments).length : 0;
  const cachedSegmentsSize = Array.isArray(cachedAssistant.segments) ? JSON.stringify(cachedAssistant.segments).length : 0;
  if (cachedSegmentsSize > targetSegmentsSize) {
    target.segments = deepClone(cachedAssistant.segments);
  }

  const targetToolCallsSize = Array.isArray(target.toolCalls) ? JSON.stringify(target.toolCalls).length : 0;
  const cachedToolCallsSize = Array.isArray(cachedAssistant.toolCalls) ? JSON.stringify(cachedAssistant.toolCalls).length : 0;
  if (cachedToolCallsSize > targetToolCallsSize) {
    target.toolCalls = deepClone(cachedAssistant.toolCalls);
  }
}

function summarizeAssistantMatchCandidate(message: any): Record<string, unknown> | null {
  if (!message || message.role !== 'assistant') return null;
  return {
    clientMessageId: message.clientMessageId || null,
    timestamp: message.timestamp || null,
    firstResponseAt: message.firstResponseAt || null,
    lastAppendedAt: message.lastAppendedAt || null,
    isRunning: message.isRunning ?? null,
    textSummary: summarizeHistoryCriticalMessages([message]).lastAssistant,
  };
}

function mergeCachedStreamingAssistantMessage(payload: any, messages: any[]): any[] {
  if (!payload?.isRunning || !payload?.sessionId) return messages;
  const cachedAssistant = findCachedStreamingAssistantMessage(payload.sessionId);
  if (!cachedAssistant) return messages;

  const nextMessages = Array.isArray(messages) ? messages : [];
  for (let index = nextMessages.length - 1; index >= 0; index -= 1) {
    const message = nextMessages[index];
    const same = cachedStreamingAssistantMatchesSnapshotMessage(cachedAssistant, message);
    if (!same) continue;
    mergeAssistantMessageFields(message, cachedAssistant);
    logSessionDebug('reply-history:merge-streaming-cache', {
      sessionId: payload.sessionId,
      action: 'matched-existing',
      index,
      cachedAssistant: summarizeAssistantMatchCandidate(cachedAssistant),
      matchedAssistant: summarizeAssistantMatchCandidate(message),
      resultSummary: summarizeHistoryCriticalMessages(nextMessages),
    });
    return nextMessages;
  }

  const lastMessage = nextMessages[nextMessages.length - 1];
  if (cachedStreamingAssistantMatchesSnapshotMessage(cachedAssistant, lastMessage)) {
    mergeAssistantMessageFields(lastMessage, cachedAssistant);
    logSessionDebug('reply-history:merge-streaming-cache', {
      sessionId: payload.sessionId,
      action: 'merged-last-assistant',
      cachedAssistant: summarizeAssistantMatchCandidate(cachedAssistant),
      matchedAssistant: summarizeAssistantMatchCandidate(lastMessage),
      resultSummary: summarizeHistoryCriticalMessages(nextMessages),
    });
    return nextMessages;
  }
  logSessionDebug('reply-history:merge-streaming-cache', {
    sessionId: payload.sessionId,
    action: 'skip-merge',
    reason: 'no-exact-assistant-match',
    cachedAssistant: summarizeAssistantMatchCandidate(cachedAssistant),
    lastAssistant: summarizeAssistantMatchCandidate(lastMessage),
    resultSummary: summarizeHistoryCriticalMessages(nextMessages),
  });
  return nextMessages;
}

export function estimateSessionMessageWeight(message: any): number {
  const content = typeof message?.content === 'string'
    ? message.content.length
    : JSON.stringify(message?.content || '').length;
  const toolCalls = Array.isArray(message?.toolCalls) ? JSON.stringify(message.toolCalls).length : 0;
  const segments = Array.isArray(message?.segments) ? JSON.stringify(message.segments).length : 0;
  const timing = `${message?.firstResponseAt || ''}${message?.lastAppendedAt || ''}`.length;
  return content + toolCalls + segments + timing + 64;
}

export function estimateSessionSnapshotWeight(snapshot: any): number {
  const base = JSON.stringify({
    title: snapshot.title || '',
    mode: snapshot.mode || '',
    model: snapshot.model || '',
    displayModel: snapshot.displayModel || '',
    agent: snapshot.agent || '',
    activeRuntime: snapshot.activeRuntime || null,
    runtimeCount: snapshot.runtimeCount || 0,
    cwd: snapshot.cwd || '',
    updated: snapshot.updated || '',
  }).length;
  return base + (snapshot.messages || []).reduce(
    (sum: number, msg: any) => sum + estimateSessionMessageWeight(msg), 0,
  );
}

export function normalizeActiveRuntime(runtime: any, fallbackAgent: string, fallbackModel = ''): any {
  if (!runtime || typeof runtime !== 'object') return null;
  const agent = normalizeAgent(runtime.agent || fallbackAgent);
  return {
    agent,
    channelKey: runtime.channelKey ?? null,
    channelLabel: runtime.channelLabel ?? null,
    mode: runtime.mode ?? null,
    model: runtime.model || '',
    displayModel: runtime.displayModel || runtime.model || fallbackModel || '',
    explicitModel: runtime.explicitModel || '',
    defaultModel: runtime.defaultModel || '',
    runtimeIdPresent: !!runtime.runtimeIdPresent,
    runtimeId: runtime.runtimeId ?? null,
    runtimeCount: Number.isFinite(runtime.runtimeCount) ? runtime.runtimeCount : 0,
  };
}

export function normalizeSessionSnapshot(payload: any, options: { complete?: boolean } = {}): any {
  const payloadDisplayModel = typeof payload?.displayModel === 'string' ? payload.displayModel.trim() : '';
  const normalizedRuntime = normalizeActiveRuntime(payload.activeRuntime, payload.agent, payloadDisplayModel || payload.model || '');
  const payloadModel = typeof payload?.model === 'string' ? payload.model.trim() : '';
  const messages = mergeCachedHistoryWindow(
    payload,
    mergeCachedStreamingAssistantMessage(payload, cloneMessages(payload.messages || [])),
    findCachedSessionMessages(payload.sessionId),
    mergeAssistantMessageFields,
  );
  return {
    sessionId: payload.sessionId,
    messages,
    title: payload.title || '新会话',
    mode: payload.mode || 'yolo',
    model: payloadModel,
    displayModel: payloadDisplayModel || normalizedRuntime?.displayModel || '',
    activeRuntime: normalizedRuntime,
    activeChannelKey: payload.activeChannelKey || normalizedRuntime?.channelKey || null,
    runtimeCount: Number.isFinite(payload.runtimeCount)
      ? payload.runtimeCount
      : (normalizedRuntime?.runtimeCount || 0),
    agent: normalizeAgent(payload.agent),
    hasUnread: !!payload.hasUnread,
    cwd: payload.cwd ?? null,
    projectId: payload.projectId ?? null,
    totalCost: typeof payload.totalCost === 'number' ? payload.totalCost : 0,
    totalUsage: payload.totalUsage ? deepClone(payload.totalUsage) : null,
    updated: payload.updated ?? null,
    isRunning: !!payload.isRunning,
    historyPending: !!payload.historyPending,
    historyTotal: Number.isFinite(payload.historyTotal) ? payload.historyTotal : ((payload.messages || []).length || 0),
    historyBuffered: Number.isFinite(payload.historyBuffered) ? payload.historyBuffered : ((payload.messages || []).length || 0),
    historyRemaining: Number.isFinite(payload.historyRemaining)
      ? payload.historyRemaining
      : Math.max(0, (Number.isFinite(payload.historyTotal) ? payload.historyTotal : ((payload.messages || []).length || 0)) - (Number.isFinite(payload.historyBuffered) ? payload.historyBuffered : ((payload.messages || []).length || 0))),
    complete: options.complete !== undefined ? !!options.complete : !payload.historyPending,
  };
}

// ---------------------------------------------------------------------------
// Session cache CRUD
// ---------------------------------------------------------------------------

export function touchSessionCache(sessionId: string): void {
  const entry = sessionState.sessionCache.get(sessionId);
  if (entry) (entry as any).lastUsed = Date.now();
}

export function invalidateSessionCache(sessionId: string): void {
  if (!sessionId) return;
  sessionState.sessionCache.delete(sessionId);
  logSessionCache('invalidate', { sessionId });
}

export function pruneSessionCache(): void {
  let totalWeight = 0;
  for (const entry of sessionState.sessionCache.values()) totalWeight += (entry as any).weight || 0;
  while (sessionState.sessionCache.size > SESSION_CACHE_LIMIT || totalWeight > SESSION_CACHE_MAX_WEIGHT) {
    let oldestId: string | null = null;
    let oldestTs = Infinity;
    const protectedIds = new Set<string>();
    if (sessionState.currentSessionId) protectedIds.add(String(sessionState.currentSessionId));
    if (sessionState.activeSessionLoad?.sessionId) protectedIds.add(String(sessionState.activeSessionLoad.sessionId));
    for (const [id, entry] of sessionState.sessionCache) {
      if (protectedIds.has(id)) continue;
      if (((entry as any).lastUsed || 0) < oldestTs) {
        oldestTs = (entry as any).lastUsed || 0;
        oldestId = id;
      }
    }
    if (!oldestId) {
      for (const [id, entry] of sessionState.sessionCache) {
        if (((entry as any).lastUsed || 0) < oldestTs) {
          oldestTs = (entry as any).lastUsed || 0;
          oldestId = id;
        }
      }
    }
    if (!oldestId) break;
    totalWeight -= (sessionState.sessionCache.get(oldestId) as any)?.weight || 0;
    sessionState.sessionCache.delete(oldestId);
    logSessionCache('prune-evict', {
      sessionId: oldestId,
      totalWeightAfter: totalWeight,
      sizeAfter: sessionState.sessionCache.size,
      protectedIds: Array.from(protectedIds),
    });
  }
}

export function cacheSessionSnapshot(snapshot: any): void {
  if (!snapshot?.sessionId) {
    logSessionCache('skip-cache', {
      sessionId: snapshot?.sessionId || null,
      reason: 'missing-session-id',
      complete: !!snapshot?.complete,
    });
    return;
  }
  const cachedSnapshot = deepClone(snapshot);
  const weight = estimateSessionSnapshotWeight(cachedSnapshot);
  if (weight > SESSION_CACHE_MAX_ENTRY_WEIGHT) {
    logSessionCache('skip-cache', {
      sessionId: cachedSnapshot.sessionId,
      reason: 'entry-too-large',
      weight,
      maxEntryWeight: SESSION_CACHE_MAX_ENTRY_WEIGHT,
    });
    invalidateSessionCache(cachedSnapshot.sessionId);
    return;
  }
  const meta = getSessionMeta(cachedSnapshot.sessionId);
  sessionState.sessionCache.set(cachedSnapshot.sessionId, {
    snapshot: cachedSnapshot,
    version: cachedSnapshot.updated ?? null,
    meta: meta ? deepClone(meta) : null,
    weight,
    lastUsed: Date.now(),
  } as any);
  logSessionCache('cache-store', {
    sessionId: cachedSnapshot.sessionId,
    weight,
    version: cachedSnapshot.updated ?? null,
    hasUnread: !!cachedSnapshot.hasUnread,
    isRunning: !!cachedSnapshot.isRunning,
    messageCount: Array.isArray(cachedSnapshot.messages) ? cachedSnapshot.messages.length : 0,
  });
  pruneSessionCache();
}

export function updateCachedSession(sessionId: string, updater: (snapshot: any) => void): void {
  const entry = sessionState.sessionCache.get(sessionId) as any;
  if (!entry) return;
  const nextSnapshot = deepClone(entry.snapshot);
  updater(nextSnapshot);
  entry.snapshot = nextSnapshot;
  entry.weight = estimateSessionSnapshotWeight(nextSnapshot);
  entry.lastUsed = Date.now();
  if (nextSnapshot.updated) entry.version = nextSnapshot.updated;
  pruneSessionCache();
}

export function reconcileSessionCacheWithSessions(): void {
  const knownIds = new Set(sessionState.sessions.map((s) => s.id));
  for (const [sessionId, entry] of sessionState.sessionCache) {
    if (!knownIds.has(sessionId)) {
      sessionState.sessionCache.delete(sessionId);
      continue;
    }
    const meta = getSessionMeta(sessionId);
    (entry as any).meta = meta ? deepClone(meta) : null;
  }
}

export function getSessionCacheDisposition(sessionId: string): 'strong' | 'weak' | 'miss' {
  const entry = sessionState.sessionCache.get(sessionId) as any;
  const meta = getSessionMeta(sessionId);
  const disposition = !entry?.snapshot || !meta
    ? 'miss'
    : (entry.snapshot.complete && entry.version === (meta.updated ?? null) && !meta.hasUnread && !meta.isRunning ? 'strong' : 'weak');
  logSessionCache('disposition', {
    sessionId,
    disposition,
    hasEntry: !!entry,
    snapshotComplete: !!entry?.snapshot?.complete,
    cachedVersion: entry?.version ?? null,
    metaUpdated: meta?.updated ?? null,
    metaHasUnread: !!meta?.hasUnread,
    metaIsRunning: !!meta?.isRunning,
  });
  if (disposition === 'strong') {
    return 'strong';
  }
  return disposition;
}

export function buildCachedSessionSnapshot(sessionId: string): any {
  const entry = sessionState.sessionCache.get(sessionId) as any;
  if (!entry?.snapshot) return null;
  const snapshot = deepClone(entry.snapshot);
  const meta = getSessionMeta(sessionId) || entry.meta;
  if (meta) {
    snapshot.title = meta.title || snapshot.title;
    snapshot.agent = normalizeAgent(meta.agent || snapshot.agent);
    snapshot.hasUnread = !!meta.hasUnread;
    snapshot.updated = meta.updated || snapshot.updated;
    snapshot.isRunning = !!meta.isRunning;
  }
  return snapshot;
}
